import { PositionLoggingAPI, UUID } from "./positionLoggingAPI";

interface Watch {
  successCallback: PositionCallback;
  errorCallback?: PositionErrorCallback | null;
}

export class PositionLoggingGeolocationProvider implements Geolocation {
  private readonly positionLoggingAPI: PositionLoggingAPI;
  private readonly sessionUUID: UUID;

  private watches: Map<number, Watch> = new Map();
  private nextWatchId = 1;
  private intervalId?: NodeJS.Timeout | null;

  constructor(positionLoggingAPI: PositionLoggingAPI, sessionUUID: UUID) {
    this.positionLoggingAPI = positionLoggingAPI;
    this.sessionUUID = sessionUUID;
  }

  private async initialisePositionReplay(): Promise<void> {
    if (this.intervalId !== undefined && this.intervalId !== null) return;

    try {
      const positions = await this.positionLoggingAPI.getPositions(
        this.sessionUUID,
      );

      if (!positions.length) return;

      let replayIndex = 0;

      const replayNextPosition = () => {
        if (replayIndex >= positions.length) {
          this.intervalId = null;
          return;
        }

        const position = positions[replayIndex];
        const nextPosition = positions[replayIndex + 1];
        replayIndex++;

        const geolocationPosition = {
          timestamp: new Date(position.timestamp).getTime(),
          coords: {
            latitude: position.position.coordinates[1],
            longitude: position.position.coordinates[0],
            altitude: position.altitude,
            accuracy: position.positionAccuracy,
            altitudeAccuracy: position.altitudeAccuracy,
            heading: position.heading,
            speed: position.speed,
          },
        } as GeolocationPosition;

        [...this.watches.values()].forEach((watch) => {
          watch.successCallback(geolocationPosition);
        });

        if (nextPosition) {
          const delay =
            new Date(nextPosition.timestamp).getTime() -
            new Date(position.timestamp).getTime();

          this.intervalId = setTimeout(replayNextPosition, delay);
        } else {
          console.warn("Position replay finished.");
          this.intervalId = null;
        }
      };

      replayNextPosition();
    } catch {
      const geolocationPositionError = {
        code: GeolocationPositionError.POSITION_UNAVAILABLE,
        message: "Could not get replay position",
      } as GeolocationPositionError;

      [...this.watches.values()].forEach((watch) => {
        watch.errorCallback
          ? watch.errorCallback(geolocationPositionError)
          : console.warn("A location watch didn't have an error handler!");
      });
    }
  }

  // @override
  public clearWatch(watchId: number): void {
    this.watches.delete(watchId);
  }

  // @override
  public getCurrentPosition(
    _successCallback: PositionCallback,
    _errorCallback?: PositionErrorCallback | null,
    _options?: PositionOptions,
  ): void {
    return;
  }

  // @override
  public watchPosition(
    successCallback: PositionCallback,
    errorCallback?: PositionErrorCallback | null,
    _options?: PositionOptions,
  ): number {
    const currentWatchId = this.nextWatchId;
    this.watches.set(currentWatchId, { successCallback, errorCallback });
    this.nextWatchId++;

    this.initialisePositionReplay();
    return currentWatchId;
  }
}
